/**
 * Structural SQL issues that Oracle often misreports as ORA-00907.
 * - Reserved words used as table/subquery aliases (e.g. PLAN)
 * - Multiple top-level SELECT statements in one submission
 */

import { extractFromTableRefs } from './fromClauseJoinCheck';

function offsetToLineCol(str, offset) {
  const before = str.substring(0, Math.max(0, offset));
  const lines = before.split(/\r?\n/);
  return { line: lines.length, column: (lines[lines.length - 1] || '').length + 1 };
}

/** Strip comments/strings to spaces (keep newlines for line mapping). */
function stripNoise(sql) {
  let out = '';
  let i = 0;
  const s = sql;
  while (i < s.length) {
    if (s[i] === '-' && s[i + 1] === '-') {
      out += '  ';
      i += 2;
      while (i < s.length && s[i] !== '\n') {
        out += ' ';
        i++;
      }
      continue;
    }
    if (s[i] === '/' && s[i + 1] === '*') {
      out += '  ';
      i += 2;
      while (i < s.length && !(s[i] === '*' && s[i + 1] === '/')) {
        out += s[i] === '\n' ? '\n' : ' ';
        i++;
      }
      if (i < s.length) {
        out += '  ';
        i += 2;
      }
      continue;
    }
    if (s[i] === "'" || s[i] === '"') {
      const q = s[i];
      out += q;
      i++;
      while (i < s.length) {
        if (q === "'" && s[i] === "'" && s[i + 1] === "'") {
          out += '  ';
          i += 2;
          continue;
        }
        if (s[i] === q) {
          out += q;
          i++;
          break;
        }
        out += s[i] === '\n' ? '\n' : ' ';
        i++;
      }
      continue;
    }
    out += s[i];
    i++;
  }
  return out;
}

/**
 * Oracle reserved / semi-reserved words that commonly break when used as aliases.
 * Using PLAN as a table alias is a classic ORA-00907 trigger.
 */
const RESERVED_ALIASES = new Set(
  [
    'PLAN',
    'ORDER',
    'GROUP',
    'HAVING',
    'WHERE',
    'SELECT',
    'FROM',
    'UNION',
    'MINUS',
    'INTERSECT',
    'JOIN',
    'INNER',
    'OUTER',
    'LEFT',
    'RIGHT',
    'FULL',
    'CROSS',
    'ON',
    'USING',
    'SET',
    'VALUES',
    'INTO',
    'TABLE',
    'VIEW',
    'INDEX',
    'SIZE',
    'NUMBER',
    'DATE',
    'COMMENT',
    'SHARE',
    'START',
    'CONNECT',
    'LEVEL',
    'UID',
    'MODE',
    'ACCESS',
    'CHECK',
    'UNIQUE',
    'PRIMARY',
    'FOREIGN',
    'TRIGGER',
    'PROCEDURE',
    'FUNCTION',
    'PACKAGE',
    'TYPE',
    'BODY',
    'OPTION',
    'VALIDATE',
    'CLUSTER',
    'COMPRESS',
    'RESOURCE',
    'ROW',
    'ROWS',
    'SESSION',
    'SYNONYM',
    'SYSDATE',
    'USER',
  ].map((k) => k.toUpperCase())
);

const SAFE_RENAMES = {
  PLAN: 'pln',
  ORDER: 'ord',
  GROUP: 'grp',
  DATE: 'dt',
  SIZE: 'sz',
  NUMBER: 'num',
  LEVEL: 'lvl',
  USER: 'usr',
  ROW: 'rw',
  ROWS: 'rws',
};

/**
 * @returns {null|{ error: string, suggestion: string, line: number, column: number, index: number, wrong: string, right: string }}
 */
export function findReservedAlias(sql) {
  if (!sql || typeof sql !== 'string') return null;
  const trimmed = sql.trim();
  const refs = extractFromTableRefs(trimmed);
  for (const ref of refs) {
    if (!ref.alias) continue;
    const upper = String(ref.alias).toUpperCase();
    if (!RESERVED_ALIASES.has(upper)) continue;
    // Locate alias token in SQL (best-effort)
    const re = new RegExp(`\\b(${ref.alias.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})\\b`, 'i');
    // Prefer match after ) for subquery aliases, else first match near FROM
    let index = -1;
    const sub = trimmed.match(
      new RegExp(
        `\\)\\s*(?:AS\\s+)?(${ref.alias.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})\\b`,
        'i'
      )
    );
    if (sub) {
      index = trimmed.indexOf(sub[0]) + sub[0].length - sub[1].length;
    } else {
      const m = re.exec(trimmed);
      if (m) index = m.index;
    }
    if (index < 0) index = 0;
    const { line, column } = offsetToLineCol(trimmed, index);
    const rename = SAFE_RENAMES[upper] || `${upper.toLowerCase()}_alias`;
    return {
      error:
        `Reserved word used as table alias at line ${line}, column ${column}: "${ref.alias}" is an Oracle reserved keyword` +
        (upper === 'PLAN'
          ? ' (PLAN is reserved for EXPLAIN PLAN). Oracle often reports this as ORA-00907 missing right parenthesis.'
          : '. Oracle often reports this as a misleading syntax error such as ORA-00907.'),
      suggestion:
        `Rename the alias "${ref.alias}" to "${rename}" (and update references like ${ref.alias}.column → ${rename}.column). Example: ) ${rename}`,
      line,
      column,
      index,
      wrong: ref.alias,
      right: rename,
    };
  }
  return null;
}

/**
 * Two top-level SELECTs (not in a subquery) — BIP/CloudFish can only run one.
 * UNION / MINUS / INTERSECT / EXCEPT continuations are a single statement.
 */
export function findMultipleStatements(sql) {
  if (!sql || typeof sql !== 'string') return null;
  const trimmed = sql.trim();
  const s = stripNoise(trimmed);
  let count = 0;
  let secondIndex = -1;
  const re = /\bSELECT\b/gi;
  let m;
  while ((m = re.exec(s)) !== null) {
    const before = s.slice(0, m.index);
    let depth = 0;
    for (let i = 0; i < before.length; i++) {
      if (before[i] === '(') depth++;
      else if (before[i] === ')') depth = Math.max(0, depth - 1);
    }
    if (depth !== 0) continue;

    // Same statement: … UNION [ALL] SELECT … / MINUS / INTERSECT / EXCEPT
    if (/\b(?:UNION(?:\s+ALL)?|MINUS|INTERSECT|EXCEPT)\s*$/i.test(before.replace(/\s+$/, ''))) {
      continue;
    }

    count++;
    if (count === 2) {
      secondIndex = m.index;
      break;
    }
  }
  if (secondIndex < 0) return null;
  const { line, column } = offsetToLineCol(trimmed, secondIndex);
  return {
    error:
      `Multiple SQL statements at line ${line}, column ${column}: this editor runs one SELECT at a time. A second SELECT was found.`,
    suggestion:
      'Run each SELECT separately (clear the editor and paste one query). Combining two SELECTs often produces ORA-00907 or ORA-00933.',
    line,
    column,
    index: secondIndex,
    wrong: 'SELECT',
    right: '',
  };
}

/**
 * @returns {null|{ error: string, suggestion: string, line: number, column: number, index: number, wrong: string, right: string }}
 */
export function findSqlStructureIssue(sql) {
  return findReservedAlias(sql) || findMultipleStatements(sql) || null;
}

